/**
 * Release helper — fetches latest version from our own backend API.
 *
 * The backend serves version info + per-platform download URLs from
 * app_version.json.  Download links point to /api/app/download/{platform}/{arch}
 * which performs a 302 redirect, so users never see the underlying storage URL.
 */

import { API_BASE_URL } from './api'

/* ------------------------------------------------------------------ */
/*  Public types                                                       */
/* ------------------------------------------------------------------ */

export interface ReleaseAsset {
  name: string
  downloadUrl: string     // points to /api/app/download/...
  size: number | null     // bytes (may be null if not set in manifest)
  platform: 'windows' | 'macos' | 'linux' | 'android'
  arch: string            // x86_64, aarch64, arm64-v8a
  format: string          // exe, dmg, AppImage, deb, apk
}

export interface ReleaseInfo {
  version: string         // e.g. "0.2.0"
  publishedAt: string     // ISO date
  releaseNotes: string
  assets: ReleaseAsset[]
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/** Map architecture strings to a short label. */
function archLabel(arch: string): string {
  if (arch === 'aarch64' || arch === 'arm64') return 'aarch64'
  return 'x64'
}

/** Guess the installer format from the file name. */
function guessFormat(fileName: string): string {
  const lower = fileName.toLowerCase()
  if (lower.endsWith('.exe')) return 'exe'
  if (lower.endsWith('.dmg')) return 'dmg'
  if (lower.endsWith('.appimage')) return 'AppImage'
  if (lower.endsWith('.deb')) return 'deb'
  if (lower.endsWith('.apk')) return 'apk'
  // Fallback: extension
  const ext = lower.split('.').pop() ?? ''
  return ext
}

/** Map backend platform names to our UI platform type. */
function normalisePlatform(p: string): 'windows' | 'macos' | 'linux' | 'android' | null {
  const lower = p.toLowerCase()
  if (lower === 'windows') return 'windows'
  if (lower === 'macos' || lower === 'darwin') return 'macos'
  if (lower === 'linux') return 'linux'
  if (lower === 'android') return 'android'
  return null
}

/* ------------------------------------------------------------------ */
/*  Main fetch                                                         */
/* ------------------------------------------------------------------ */

/**
 * Fetch the latest app version from our backend.
 * Returns null if the backend is unreachable or has no version info.
 */
export async function fetchLatestRelease(): Promise<ReleaseInfo | null> {
  try {
    const res = await fetch(`${API_BASE_URL}/app/version`, {
      headers: { Accept: 'application/json' },
    })

    if (!res.ok) throw new Error(`Backend API ${res.status}`)

    const data = await res.json()

    // data matches AppVersionResponse schema:
    // { version, release_date, release_notes, mandatory, assets[] }

    const assets: ReleaseAsset[] = (data.assets ?? [])
      .map(
        (a: {
          platform: string
          architecture: string
          download_url: string
          file_name: string
          file_size: number | null
        }) => {
          const platform = normalisePlatform(a.platform)
          if (!platform) return null

          // Build a proxy download URL so the user never hits GitHub directly
          const downloadUrl = `${API_BASE_URL}/app/download/${a.platform}/${a.architecture}`

          return {
            name: a.file_name,
            downloadUrl,
            size: a.file_size ?? 0,
            platform,
            arch: archLabel(a.architecture),
            format: guessFormat(a.file_name),
          } satisfies ReleaseAsset
        },
      )
      .filter(Boolean) as ReleaseAsset[]

    return {
      version: data.version,
      publishedAt: data.release_date,
      releaseNotes: data.release_notes ?? '',
      assets,
    }
  } catch (err) {
    console.error('Failed to fetch latest release:', err)
    return null
  }
}

/* ------------------------------------------------------------------ */
/*  Utility                                                            */
/* ------------------------------------------------------------------ */

/**
 * Try to detect the visitor's OS for pre-selecting the right tab.
 */
export function detectOS(): 'windows' | 'macos' | 'linux' | 'android' {
  const ua = navigator.userAgent.toLowerCase()
  if (ua.includes('android')) return 'android'
  if (ua.includes('mac')) return 'macos'
  if (ua.includes('linux')) return 'linux'
  return 'windows'
}

/**
 * Human-readable file size.
 */
export function formatBytes(bytes: number): string {
  if (!bytes || bytes === 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}
